/**
 * <variant-add-list>
 *
 * Renders a checkbox per variant row. Checking one or more rows enables the
 * page's single/global "Add to Cart" button (marked with
 * [data-global-add-to-cart]). Clicking that button adds ALL checked variants
 * to the cart together, in one /cart/add.js call using the `items` array.
 *
 * LOCKED DEFAULT VARIANT
 * -----------------------
 * The row(s) rendered with `data-locked="true"` on their checkbox represent
 * a variant that must ALWAYS be included in the cart submission:
 *   - It is checked on page load (set in the Liquid template).
 *   - Any attempt to uncheck it (click, keyboard, programmatic) is reverted
 *     immediately.
 *   - As a safety net, addCheckedVariantsToCart() force-includes locked
 *     variant ids in the submitted `items` array even if the DOM state
 *     were ever somehow altered.
 *
 * BUNDLE GROUPING (cart-side)
 * -----------------------------
 * Every time the global Add to Cart button is clicked, ALL variants checked
 * at that moment (including the locked default) are tagged with the same
 * shared line item property `_bundle_id` (a random id unique to that click).
 * This lets the cart / cart-drawer template visually group those line items
 * together as a single "bundle card" and display a combined subtotal for
 * that group, while Shopify's actual cart + checkout totals are computed
 * normally as the sum of the individual line items (nothing about pricing
 * or checkout behavior is altered — this is purely a grouping key).
 *
 * LIVE CART UPDATE — matches this theme's native flow exactly:
 * The theme's <cart-notification> element (see theme.js -> CartNotification)
 * refreshes itself via Shopify's Section Rendering API: any /cart/add.js (or
 * /cart/change.js) request that includes `sections` + `sections_url` in its
 * body gets back a `sections` object in the response, keyed by section id.
 * The theme then does:
 *
 *   elementToReplace.innerHTML = html.querySelector('#minicart-form').innerHTML;
 *
 * ...for the 'minicart-form' section, followed by cart.cartAction() to
 * re-bind event listeners on the freshly injected markup, and cart.open()
 * to show the drawer/popup.
 *
 * This file reproduces that exact sequence so the minicart, cart icon count,
 * free-shipping bar, and upsell drawer all behave identically to a native
 * Shopify add-to-cart submission.
 */
class VariantAddList extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.formId = this.dataset.formId;
    this.productId = this.dataset.productId;
    this.onCheckboxChange = this.onCheckboxChange.bind(this);
    this.onCheckboxClick = this.onCheckboxClick.bind(this);
  }

  connectedCallback() {
    // Guarantee the locked row(s) are checked as soon as this element is
    // connected, in case the browser restored a stale form state (bfcache,
    // autofill, etc.) that could have left them unchecked.
    this.enforceLockedCheckboxes();

    // "change" fires after the checkbox state has already flipped, so we
    // use it to revert an illegitimate uncheck. "click" is also listened to
    // (capture phase) so we can stop the event before it even toggles the
    // native checkbox state where possible.
    this.addEventListener('change', this.onCheckboxChange);
    this.addEventListener('click', this.onCheckboxClick, true);

    this.globalButton = this.findGlobalAddToCartButton();
    this.cartNotification =
      document.querySelector('cart-notification') ||
      document.querySelector('cart-drawer');
    this.mediaGallery = this.findMediaGallery();

    if (this.globalButton) {
      this.globalButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.addCheckedVariantsToCart();
      });
    } else {
      console.warn(
        '[variant-add-list] Could not find the global Add to Cart button. ' +
          'Add the attribute data-global-add-to-cart to it (see INTEGRATION-INSTRUCTIONS.md).'
      );
    }

    this.updateGlobalButtonState();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onCheckboxChange);
    this.removeEventListener('click', this.onCheckboxClick, true);
  }

  /**
   * Forces every checkbox marked data-locked="true" to be checked, and
   * makes sure its row carries the visual "checked" class.
   */
  enforceLockedCheckboxes() {
    this.querySelectorAll('[data-variant-checkbox][data-locked="true"]').forEach((input) => {
      if (!input.checked) input.checked = true;
      const row = input.closest('[data-variant-row]');
      if (row) row.classList.add('variant-add-list__row--checked');
    });
  }

  getLockedVariantIds() {
    return Array.from(this.querySelectorAll('[data-variant-checkbox][data-locked="true"]')).map(
      (input) => input.value
    );
  }

  /**
   * Click-phase guard (capture: true). If the user clicks directly on a
   * locked checkbox (or its row label) trying to uncheck it, stop the
   * event outright so the native toggle never happens in the first place.
   */
  onCheckboxClick(event) {
    const input = event.target.matches('[data-variant-checkbox]')
      ? event.target
      : null;

    if (input && input.dataset.locked === 'true' && input.checked) {
      // Currently checked and locked -> clicking it would uncheck it.
      // Prevent that entirely.
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Change-phase safety net. Covers any path that isn't a direct click
   * (keyboard space/enter on a focused checkbox, programmatic .click(),
   * label activation, etc.). If a locked checkbox somehow ends up
   * unchecked, immediately revert it.
   */
  onCheckboxChange(event) {
    if (!event.target.matches('[data-variant-checkbox]')) return;

    const input = event.target;
    const row = input.closest('[data-variant-row]');

    if (input.dataset.locked === 'true' && !input.checked) {
      input.checked = true;
      if (row) row.classList.add('variant-add-list__row--checked');
      this.updateGlobalButtonState();
      return;
    }

    if (row) {
      row.classList.toggle('variant-add-list__row--checked', input.checked);

      // Show this variant's image (last-checked wins) using the same
      // gallery methods the theme's own swatches use, so Swiper stays in
      // a consistent state.
      if (input.checked) {
        this.showVariantImage(row);
      }
    }

    this.updateGlobalButtonState();
  }

  findGlobalAddToCartButton() {
    let button = document.querySelector(
      `[data-global-add-to-cart][data-section-id="${this.sectionId}"]`
    );
    if (button) return button;

    button = document.querySelector('[data-global-add-to-cart]');
    if (button) return button;

    if (this.formId) {
      const form = document.getElementById(this.formId);
      if (form) {
        return form.querySelector('button[name="add"], .product_submit_button');
      }
    }

    return document.querySelector('.product_submit_button');
  }

  getCheckedVariantIds() {
    const checked = new Set(
      Array.from(this.querySelectorAll('[data-variant-checkbox]:checked')).map(
        (input) => input.value
      )
    );

    // Safety net: force-include locked variant ids even if, somehow, their
    // checkbox state was not checked at submission time.
    this.getLockedVariantIds().forEach((id) => checked.add(id));

    return Array.from(checked);
  }

  /**
   * Returns true if the given variant id's price is $0.00, based on the
   * data-variant-price attribute on its checkbox row. Free variants are
   * capped at quantity 1 per add-to-cart click (see addCheckedVariantsToCart).
   */
  isFreeVariant(variantId) {
    const input = this.querySelector(
      `[data-variant-checkbox][value="${variantId}"]`
    );
    if (!input) return false;
    const price = parseFloat(input.dataset.variantPrice);
    return !Number.isNaN(price) && price === 0;
  }

  /**
   * Returns true if the given variant id's price is $0.00, based on the
   * data-variant-price attribute on its checkbox row (in cents, per
   * Shopify's Liquid `variant.price`). Free variants are capped at
   * quantity 1 per Add to Cart click regardless of the global quantity
   * selector's value.
   */
  isFreeVariant(variantId) {
    const input = this.querySelector(
      `[data-variant-checkbox][value="${variantId}"]`
    );
    if (!input) return false;
    const price = parseFloat(input.dataset.variantPrice);
    return !Number.isNaN(price) && price === 0;
  }

  getQuantity() {
    const qtyInput = this.formId
      ? document.querySelector(`#${this.formId} input[name="quantity"]`)
      : document.querySelector('input[name="quantity"]');
    const value = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    return Number.isNaN(value) || value < 1 ? 1 : value;
  }

  findMediaGallery() {
    // Prefer the gallery scoped to this same product form/section.
    const scoped = document.querySelector(
      `#MainProduct-${this.sectionId} media-gallery`
    );
    if (scoped) return scoped;

    return document.querySelector('media-gallery');
  }

  showVariantImage(row) {
    const mediaId = row.getAttribute('data-media-id');
    if (!mediaId || !this.mediaGallery) return;

    const slideWithThumbs = this.mediaGallery.querySelector('slide-with-thumbs');
    const position = row.getAttribute('data-media-position');

    if (slideWithThumbs) {
      // Thumbnail/Swiper layout (thumbnail_left, thumbnail_bottom,
      // hidden_thumbnail). There is no #GalleryViewer element in this
      // layout, so MediaGallery.setActiveMedia would throw — only use
      // the slider's own navigation method here.
      if (position !== null && typeof slideWithThumbs.functionGoto === 'function') {
        slideWithThumbs.functionGoto(Number(position));
      }
      return;
    }

    // Grid / stack layout: #GalleryViewer-<section> exists, so this is
    // the method the theme's own swatches use here.
    const galleryViewer = this.mediaGallery.querySelector('[id^="GalleryViewer"]');
    if (galleryViewer && typeof this.mediaGallery.setActiveMedia === 'function') {
      this.mediaGallery.setActiveMedia(mediaId, true);
    }
  }

  updateGlobalButtonState() {
    if (!this.globalButton) return;

    // The locked default variant means there is ALWAYS at least one
    // selection, so the button should never be disabled for "no selection"
    // reasons anymore. Kept as a guard in case markup ever ships with zero
    // locked/checked rows.
    const hasSelection = this.getCheckedVariantIds().length > 0;
    this.globalButton.disabled = !hasSelection;
    this.globalButton.classList.toggle('variant-add-list__global-btn--disabled', !hasSelection);
  }

  setGlobalButtonLoading(isLoading) {
    if (!this.globalButton) return;

    if (isLoading) {
      this.globalButton.classList.add('variant-add-list__global-btn--loading', 'loading');
      this.globalButton.setAttribute('aria-disabled', 'true');
      this.globalButton.disabled = true;
    } else {
      this.globalButton.classList.remove('variant-add-list__global-btn--loading', 'loading');
      this.globalButton.removeAttribute('aria-disabled');
      this.updateGlobalButtonState();
    }
  }

  /**
   * Returns the sections this component needs re-rendered after adding to
   * cart. Mirrors CartNotification.getSectionsToRender() in theme.js.
   */
  getSectionsToRender() {
    if (this.cartNotification && typeof this.cartNotification.getSectionsToRender === 'function') {
      return this.cartNotification.getSectionsToRender();
    }
    return [{ id: 'minicart-form' }];
  }

  /**
   * Generates a short random id used to tag every line item added in a
   * single Add to Cart click, so the cart/cart-drawer template can group
   * them visually as one "bundle" and show a combined subtotal.
   */
  generateBundleId() {
    return `bundle_${this.productId}`;
  }

  async addCheckedVariantsToCart() {
    const variantIds = this.getCheckedVariantIds();
    if (variantIds.length === 0) return;

    const quantity = this.getQuantity();
    const bundleId = this.generateBundleId();
    const bundleLabel = this.getBundleLabel();

    this.setGlobalButtonLoading(true);

    const cartRecommend = document.querySelector('.cart-recommend');
    if (cartRecommend && cartRecommend.classList.contains('open')) {
      cartRecommend.classList.remove('open');
    }

    try {
        const body = JSON.stringify({
        items: variantIds.map((id) => ({
          id,
          quantity: this.isFreeVariant(id) ? 1 : quantity,
          properties: {
            _bundle_id: bundleId,
            _bundle_product: bundleLabel,
          },
        })),
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body,
      });

      const parsedState = await response.json();

      if (!response.ok || parsedState.status) {
        throw new Error(
          parsedState.description || parsedState.message || 'Could not add items to cart'
        );
      }

      // Uncheck rows after a successful add, EXCEPT the locked default
      // row, which must remain checked/ready for the next add.
      this.querySelectorAll('[data-variant-checkbox]:checked').forEach((input) => {
        if (input.dataset.locked === 'true') return;
        input.checked = false;
        const row = input.closest('[data-variant-row]');
        if (row) row.classList.remove('variant-add-list__row--checked');
      });
      this.enforceLockedCheckboxes();

      this.renderSections(parsedState);
      await this.refreshCartCount();

      if (this.cartNotification) {
        if (typeof this.cartNotification.cartAction === 'function') {
          this.cartNotification.cartAction();
        }
        if (this.cartNotification.classList.contains('is-empty')) {
          this.cartNotification.classList.remove('is-empty');
        }
        if (typeof this.cartNotification.open === 'function') {
          this.cartNotification.open();
        }
      }

      this.dispatchEvent(
        new CustomEvent('variant-add-list:added', {
          bubbles: true,
          detail: { variantIds, quantity, bundleId },
        })
      );
    } catch (error) {
      console.error('[variant-add-list] add to cart failed:', error);
      this.dispatchEvent(
        new CustomEvent('variant-add-list:error', {
          bubbles: true,
          detail: { message: error.message },
        })
      );
    } finally {
      this.setGlobalButtonLoading(false);
      const cartRecommend2 = document.querySelector('.cart-recommend');
      if (cartRecommend2 && !cartRecommend2.classList.contains('hidden-recommend')) {
        window.setTimeout(() => {
          cartRecommend2.classList.add('open');
        }, 800);
      }
    }
  }

  getBundleLabel() {
    const productJsonEl = document.querySelector(
      `#MainProduct-${this.sectionId} .productJson`
    );
    if (productJsonEl) {
      try {
        const productData = JSON.parse(productJsonEl.textContent);
        if (productData && productData.title) return productData.title;
      } catch (e) {
        /* noop, fall through to default label below */
      }
    }
    return document.title;
  }

  /**
   * Injects the fresh section HTML returned by /cart/add.js, exactly like
   * CartNotification does: swap #minicart-form's innerHTML.
   */
  renderSections(parsedState) {
    if (!parsedState.sections) return;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) return;

      const elementToReplace = document.getElementById(section.id);
      if (!elementToReplace) return;

      const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const freshMinicartForm = html.querySelector('#minicart-form');
      if (freshMinicartForm) {
        elementToReplace.innerHTML = freshMinicartForm.innerHTML;
      }

      // Re-run bundle grouping on the freshly injected minicart markup.
      if (window.CartBundleGrouping && typeof window.CartBundleGrouping.run === 'function') {
        window.CartBundleGrouping.run(elementToReplace);
      }
    });
  }

  /**
   * Updates cart count bubble(s) and header total price, same as the
   * theme's own ProductForm.onSubmitHandler does after a successful add.
   */
  async refreshCartCount() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (cart.item_count !== undefined) {
        document.querySelectorAll('.cart-count').forEach((el) => {
          if (el.classList.contains('cart-count-drawer')) {
            el.innerHTML = `(${cart.item_count})`;
          } else {
            el.innerHTML = cart.item_count > 100 ? '~' : cart.item_count;
          }
        });

        const headerTotalPrice = document.querySelector('header-total-price');
        if (headerTotalPrice && typeof headerTotalPrice.updateTotal === 'function') {
          headerTotalPrice.updateTotal(cart);
        }

        const cartFreeShip = document.querySelector('free-ship-progress-bar');
        if (cartFreeShip && typeof cartFreeShip.init === 'function') {
          cartFreeShip.init(cart.items_subtotal_price);
        }
      }

      return cart;
    } catch (error) {
      console.error('[variant-add-list] cart count refresh failed:', error);
      return null;
    }
  }
}

if (!customElements.get('variant-add-list')) {
  customElements.define('variant-add-list', VariantAddList);
}
